/**
 * What the player can do right now, decided by the sim (§16 proximity, §14
 * the affordance list, §19 "the UI reads state, never computes it").
 *
 * The UI asks this and draws buttons. It never works out for itself
 * whether a diner sells food, whether Dee is behind the counter, or whether
 * a shift is open: every one of those is a rule that lives here or deeper.
 */

import type { Action } from './actions.js';
import { conversationMode, type ConversationMode } from '../engine/conversation.js';
import { isPresent, npcsAt } from './presence.js';
import { step } from './reducer.js';
import { TUNING } from './tuning.js';
import type { Fact, Job, NPC, ViolenceVerb, WorldState } from './types/index.js';
import { sceneVerbs } from './violence.js';

export type Affordance =
  | { readonly kind: 'talk'; readonly npc: NPC; readonly mode: ConversationMode; readonly minutes: number; readonly action: Action }
  /** Pay a money debt to the creditor in front of you. */
  | { readonly kind: 'pay'; readonly fact: Extract<Fact, { kind: 'obligation' }>; readonly creditor: NPC; readonly amount: number; readonly action: Action }
  | { readonly kind: 'eat'; readonly price: number; readonly action: Action }
  | { readonly kind: 'sleep'; readonly inBed: boolean; readonly action: Action }
  | { readonly kind: 'work'; readonly job: Job; readonly action: Action }
  | { readonly kind: 'wait'; readonly action: Action }
  /** Start a fight with someone (§17). */
  | { readonly kind: 'fight'; readonly npc: NPC; readonly action: Action }
  /** Lean on someone for money (§17 shakedown). */
  | { readonly kind: 'shakedown'; readonly npc: NPC; readonly amount: number; readonly action: Action }
  /** One verb of an open scene. */
  | { readonly kind: 'beat'; readonly verb: ViolenceVerb; readonly action: Action };

const SLEEP_MINUTES = 8 * 60;
const WAIT_MINUTES = 30;

/** Would the sim accept this action right now? Dry-run, no side effects. */
function possible(state: WorldState, action: Action): boolean {
  return step(state, action).report.ok;
}

/** What a shakedown asks for by default: what he could actually hand over. */
const SHAKEDOWN_ASK = 20;

export function affordances(state: WorldState): readonly Affordance[] {
  const out: Affordance[] = [];
  const here = state.player.position.at;

  // Mid-scene, the verb list is the whole world (§17).
  if (state.scene !== null) {
    for (const verb of sceneVerbs(state.scene)) out.push({ kind: 'beat', verb, action: { type: 'beat', verb } });
    return out;
  }

  // Mid-conversation, there is nothing to offer (§14): the player's next
  // line is free text into the composer, not a choice off this list, and no
  // `Affordance` kind models "send a line" or "leave" one. Every other
  // affordance below — talk to someone else, wait, eat, work, a fight — would
  // be a way to silently abandon (or blindside) the person still mid-scene
  // with you, so "nothing else is legal" means an empty list, not a
  // substitute one, exactly as `state.scene` above.
  if (state.activeConversation !== null) return [];

  if (here !== null) {
    for (const id of npcsAt(state, here)) {
      const npc = state.npcs[id];
      if (!npc || !isPresent(state, id)) continue;
      // A conversation costs what it costs (§9): a word, or an hour.
      for (const minutes of [TUNING.talk.wordMinutes, TUNING.talk.sitMinutes]) {
        const action: Action = { type: 'talk', npc: id, minutes };
        if (possible(state, action)) out.push({ kind: 'talk', npc, mode: conversationMode(state, id), minutes, action });
      }
      for (const fact of Object.values(state.memory.facts)) {
        if (fact.kind !== 'obligation' || fact.settled || fact.to !== id || fact.substance.kind !== 'money') continue;
        const action: Action = { type: 'pay', fact: fact.id };
        if (possible(state, action)) out.push({ kind: 'pay', fact, creditor: npc, amount: fact.substance.amount, action });
      }
      const fight: Action = { type: 'start_scene', kind: 'fistfight', npc: id };
      if (possible(state, fight)) out.push({ kind: 'fight', npc, action: fight });
      const amount = Math.min(SHAKEDOWN_ASK, Math.floor(npc.truth.money));
      const lean: Action = { type: 'start_scene', kind: 'shakedown', npc: id, demand: amount };
      if (amount > 0 && possible(state, lean)) out.push({ kind: 'shakedown', npc, amount, action: lean });
    }

    for (const job of Object.values(state.jobs)) {
      if (job.at !== here) continue;
      const action: Action = { type: 'work', job: job.id };
      if (possible(state, action)) out.push({ kind: 'work', job, action });
    }

    const eat: Action = { type: 'eat' };
    if (possible(state, eat)) {
      out.push({ kind: 'eat', price: Math.round(TUNING.needs.mealPrice * state.economy.priceIndex * 100) / 100, action: eat });
    }
  }

  const sleep: Action = { type: 'sleep', minutes: SLEEP_MINUTES };
  if (possible(state, sleep)) {
    out.push({ kind: 'sleep', inBed: state.player.lodging !== null && here === state.player.lodging, action: sleep });
  }

  const wait: Action = { type: 'wait', minutes: WAIT_MINUTES };
  if (possible(state, wait)) out.push({ kind: 'wait', action: wait });

  return out;
}
